import { UseFormGetValues, UseFormSetValue } from 'react-hook-form';

import { CANADA_PROVINCE_ABBREVIATIONS } from 'constants/CanadaProvinceTimezones';
import { US_STATE_ABBREVIATIONS } from 'constants/USStateTimezones';
import {
  LTLAccessorial,
  LTLSelectedAccessorial,
  QuickQuoteInputs,
  QuoteCountries,
  QuoteFormStop,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { StopTypes } from 'types/Appointment';
import { TMSLocation } from 'types/Load';

import {
  findAccessorialById,
  getAccessorialId,
  sortSelectedAccessorials,
} from '../accessorialUtils';

const fieldSetOptions = {
  shouldDirty: true,
  shouldValidate: false,
} as const;

function resolveCountryFromState(state: string): QuoteCountries {
  const upper = state.trim().toUpperCase();
  if (CANADA_PROVINCE_ABBREVIATIONS.includes(upper)) {
    return QuoteCountries.Canada;
  }
  if (US_STATE_ABBREVIATIONS.includes(upper)) {
    return QuoteCountries.USA;
  }
  return QuoteCountries.USA;
}

export function formatTaiAddressBookLocationText(
  location: TMSLocation
): string {
  const street = [location.addressLine1, location.addressLine2]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(', ');
  const city = (location.city ?? '').trim();
  const state = (location.state ?? '').trim();
  const zip = (location.zipCode ?? '').trim();

  const cityStateZip = [city, state && zip ? `${state} ${zip}` : state || zip]
    .filter(Boolean)
    .join(', ');

  return [street, cityStateZip].filter(Boolean).join(', ');
}

export function buildQuoteFormStopFromAddressBook(
  stop: QuoteFormStop,
  location: TMSLocation
): QuoteFormStop {
  const state = (location.state ?? '').trim();
  const locationText = formatTaiAddressBookLocationText(location);

  return {
    ...stop,
    externalTMSID: location.externalTMSID,
    addressName: (location.name ?? '').trim(),
    addressLine1: (location.addressLine1 ?? '').trim(),
    addressLine2: (location.addressLine2 ?? '').trim(),
    city: (location.city ?? '').trim(),
    state,
    zip: (location.zipCode ?? '').trim(),
    country: resolveCountryFromState(state),
    location: locationText,
    locationDisplay:
      (location.name ?? '').trim() ||
      (location.nameAddress ?? '').trim() ||
      locationText,
  };
}

export function mergeAddressBookAccessorials(
  selectedAccessorials: LTLSelectedAccessorial[],
  accessorialIds: string[],
  catalog: LTLAccessorial[],
  fromAddressBookStop?: StopTypes
): LTLSelectedAccessorial[] {
  if (accessorialIds.length === 0) {
    return selectedAccessorials;
  }

  const selectedIds = new Set<string>();
  for (const selection of selectedAccessorials) {
    const code = (selection.code ?? '').trim();
    if (code) {
      selectedIds.add(code);
    }
    if (selection.externalId != null) {
      selectedIds.add(String(selection.externalId));
    }
  }

  const next = [...selectedAccessorials];
  for (const rawId of accessorialIds) {
    const id = String(rawId ?? '').trim();
    if (!id || selectedIds.has(id)) {
      continue;
    }

    const fromCatalog = findAccessorialById(catalog, id);
    if (fromCatalog) {
      const catalogId = getAccessorialId(fromCatalog);
      if (
        selectedIds.has(catalogId) ||
        (fromCatalog.externalId != null &&
          selectedIds.has(String(fromCatalog.externalId)))
      ) {
        continue;
      }

      const selection: LTLSelectedAccessorial = {
        code: fromCatalog.code,
        name: fromCatalog.name,
        externalId: fromCatalog.externalId,
        ...(fromAddressBookStop ? { fromAddressBookStop } : {}),
      };
      if (fromCatalog.actions) {
        selection.actionValues = Object.fromEntries(
          Object.keys(fromCatalog.actions).map((key) => [key, ''])
        );
      }
      next.push(selection);
      selectedIds.add(catalogId);
      if (fromCatalog.externalId != null) {
        selectedIds.add(String(fromCatalog.externalId));
      }
      continue;
    }

    // Drop IDs missing from the pricing catalog; do not emit Name:"123" placeholders.
    continue;
  }

  if (next.length === selectedAccessorials.length) {
    return selectedAccessorials;
  }

  return catalog.length > 0 ? sortSelectedAccessorials(next, catalog) : next;
}

function otherAddressBookStop(stop: StopTypes): StopTypes {
  return stop === StopTypes.Pickup ? StopTypes.Dropoff : StopTypes.Pickup;
}

function addressBookStopForIndex(stopIndex: number): StopTypes {
  return stopIndex === 0 ? StopTypes.Pickup : StopTypes.Dropoff;
}

/**
 * Drops the accessorials a stop's favorite added. One the other stop's current
 * favorite also lists stays selected and moves to that stop: the merge tags an
 * accessorial once, so it carries only the first favorite's stop.
 * Manual and Shiplify selections are never touched.
 */
export function removeAddressBookAccessorials(
  selectedAccessorials: LTLSelectedAccessorial[],
  stop: StopTypes,
  otherStopAccessorialIds: string[],
  catalog: LTLAccessorial[]
): LTLSelectedAccessorial[] {
  const otherStopIds = new Set<string>();
  for (const rawId of otherStopAccessorialIds) {
    const id = String(rawId ?? '').trim();
    if (!id) {
      continue;
    }
    otherStopIds.add(id);
    const fromCatalog = findAccessorialById(catalog, id);
    if (fromCatalog) {
      otherStopIds.add(getAccessorialId(fromCatalog));
      if (fromCatalog.externalId != null) {
        otherStopIds.add(String(fromCatalog.externalId));
      }
    }
  }

  let changed = false;
  const next: LTLSelectedAccessorial[] = [];
  for (const selection of selectedAccessorials) {
    if (selection.fromAddressBookStop !== stop) {
      next.push(selection);
      continue;
    }

    changed = true;
    const code = (selection.code ?? '').trim();
    const listedByOtherStop =
      (code !== '' && otherStopIds.has(code)) ||
      (selection.externalId != null &&
        otherStopIds.has(String(selection.externalId)));
    if (listedByOtherStop) {
      next.push({
        ...selection,
        fromAddressBookStop: otherAddressBookStop(stop),
      });
    }
  }

  return changed ? next : selectedAccessorials;
}

/** Removes a stop's address-book accessorials when its favorite is cleared. */
export function clearTaiAddressBookAccessorialsFromForm(
  stopIndex: number,
  getValues: UseFormGetValues<QuickQuoteInputs>,
  setValue: UseFormSetValue<QuickQuoteInputs>,
  otherStopAccessorialIds: string[] = [],
  accessorialCatalog: LTLAccessorial[] = []
): void {
  const selected = getValues('ltl.selectedAccessorials') ?? [];
  const next = removeAddressBookAccessorials(
    selected,
    addressBookStopForIndex(stopIndex),
    otherStopAccessorialIds,
    accessorialCatalog
  );
  if (next !== selected) {
    setValue('ltl.selectedAccessorials', next, fieldSetOptions);
  }
}

export function isEmptyTaiCustomerScopeKey(scopeKey: string): boolean {
  const separatorIndex = scopeKey.indexOf(':');
  if (separatorIndex < 0) {
    return !scopeKey.trim();
  }
  const dbId = scopeKey.slice(0, separatorIndex).trim();
  const externalId = scopeKey.slice(separatorIndex + 1).trim();
  return !dbId && !externalId;
}

export function clearTaiAddressBookPrefillFromForm(
  stopIndex: number,
  setValue: UseFormSetValue<QuickQuoteInputs>
): void {
  const clearOptions = {
    shouldDirty: true,
    shouldValidate: false,
  } as const;

  setValue(`stops.${stopIndex}.externalTMSID`, '', clearOptions);
  setValue(`stops.${stopIndex}.addressName`, '', clearOptions);
  setValue(`stops.${stopIndex}.addressLine1`, '', clearOptions);
  setValue(`stops.${stopIndex}.addressLine2`, '', clearOptions);
  setValue(`stops.${stopIndex}.city`, '', clearOptions);
  setValue(`stops.${stopIndex}.state`, '', clearOptions);
  setValue(`stops.${stopIndex}.zip`, '', clearOptions);
  setValue(`stops.${stopIndex}.country`, undefined, clearOptions);
  setValue(`stops.${stopIndex}.location`, '', clearOptions);
  setValue(`stops.${stopIndex}.locationDisplay`, '', clearOptions);
}

export function applyTaiAddressBookLocationToForm(
  stopIndex: number,
  location: TMSLocation,
  getValues: UseFormGetValues<QuickQuoteInputs>,
  setValue: UseFormSetValue<QuickQuoteInputs>,
  accessorialCatalog: LTLAccessorial[] = [],
  otherStopAccessorialIds: string[] = []
): void {
  const stop = getValues(`stops.${stopIndex}`);
  const updated = buildQuoteFormStopFromAddressBook(stop, location);

  setValue(
    `stops.${stopIndex}.externalTMSID`,
    updated.externalTMSID,
    fieldSetOptions
  );
  setValue(
    `stops.${stopIndex}.addressName`,
    updated.addressName ?? '',
    fieldSetOptions
  );
  setValue(
    `stops.${stopIndex}.addressLine1`,
    updated.addressLine1 ?? '',
    fieldSetOptions
  );
  setValue(
    `stops.${stopIndex}.addressLine2`,
    updated.addressLine2 ?? '',
    fieldSetOptions
  );
  setValue(`stops.${stopIndex}.city`, updated.city, fieldSetOptions);
  setValue(`stops.${stopIndex}.state`, updated.state, fieldSetOptions);
  setValue(`stops.${stopIndex}.zip`, updated.zip, fieldSetOptions);
  setValue(`stops.${stopIndex}.country`, updated.country, fieldSetOptions);
  setValue(
    `stops.${stopIndex}.location`,
    updated.location ?? '',
    fieldSetOptions
  );
  setValue(
    `stops.${stopIndex}.locationDisplay`,
    updated.locationDisplay ?? '',
    fieldSetOptions
  );

  applyTaiAddressBookAccessorialsToForm(
    stopIndex,
    location,
    getValues,
    setValue,
    accessorialCatalog,
    otherStopAccessorialIds
  );
}

// Adds a favorite's accessorials, replacing the ones the stop's previous favorite
// added. Separate from the address prefill so it can run again once the catalog loads.
export function applyTaiAddressBookAccessorialsToForm(
  stopIndex: number,
  location: TMSLocation,
  getValues: UseFormGetValues<QuickQuoteInputs>,
  setValue: UseFormSetValue<QuickQuoteInputs>,
  accessorialCatalog: LTLAccessorial[] = [],
  otherStopAccessorialIds: string[] = []
): void {
  const fromAddressBookStop = addressBookStopForIndex(stopIndex);
  const selected = getValues('ltl.selectedAccessorials') ?? [];
  const merged = mergeAddressBookAccessorials(
    removeAddressBookAccessorials(
      selected,
      fromAddressBookStop,
      otherStopAccessorialIds,
      accessorialCatalog
    ),
    (location.accessorials ?? []).map(String),
    accessorialCatalog,
    fromAddressBookStop
  );
  if (merged !== selected) {
    setValue('ltl.selectedAccessorials', merged, fieldSetOptions);
  }
}
